import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";

interface LegalDocument {
  id: string;
  type: string;
  version: string;
  content: string;
  published_at: string;
  is_active: boolean;
}

interface PendingDocuments {
  privacyPolicy: LegalDocument | null;
  terms: LegalDocument | null;
}

interface LegalAcceptanceModalProps {
  open: boolean;
  pendingDocuments: PendingDocuments;
  onAccept: () => void;
}

const LegalAcceptanceModal = ({ open, pendingDocuments, onAccept }: LegalAcceptanceModalProps) => {
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const [isAccepting, setIsAccepting] = useState(false);
  const [hasAgreedPrivacy, setHasAgreedPrivacy] = useState(false);
  const [hasAgreedTerms, setHasAgreedTerms] = useState(false);

  const needsPrivacy = !!pendingDocuments.privacyPolicy;
  const needsTerms = !!pendingDocuments.terms;

  // Reset checkboxes when modal opens
  useEffect(() => {
    if (open) {
      setHasAgreedPrivacy(false);
      setHasAgreedTerms(false);
    }
  }, [open]);

  const canAccept = 
    (!needsPrivacy || hasAgreedPrivacy) && 
    (!needsTerms || hasAgreedTerms);

  const handleAccept = async () => {
    if (!user) return;

    setIsAccepting(true);
    try {
      const updates: Record<string, string> = {};

      if (needsPrivacy && pendingDocuments.privacyPolicy) {
        updates.accepted_privacy_policy_version = pendingDocuments.privacyPolicy.version;
        updates.accepted_privacy_policy_at = new Date().toISOString();
      }

      if (needsTerms && pendingDocuments.terms) {
        updates.accepted_terms_version = pendingDocuments.terms.version;
        updates.accepted_terms_at = new Date().toISOString();
      }

      const { error } = await supabase
        .from("fan_profiles")
        .update(updates)
        .eq("user_id", user.id);

      if (error) {
        console.log("No fan profile found, acceptance will be recorded later");
      }

      toast.success("Legal documents accepted");
      onAccept();
    } catch (err) {
      console.error("Error accepting legal documents:", err);
      toast.error("Failed to record acceptance. Please try again.");
    } finally {
      setIsAccepting(false);
    }
  };

  const handleDecline = async () => {
    await signOut();
    navigate("/");
    toast.info("You must accept our legal documents to use Bortal");
  };

  const renderContentPreview = (content: string) => {
    const lines = content.split('\n');
    return lines.slice(0, 30).map((line, index) => {
      const trimmedLine = line.trim();
      if (!trimmedLine) return <div key={index} className="h-2" />;
      
      if (trimmedLine.includes('for Bortal')) {
        return <h2 key={index} className="text-lg font-bold mb-2">{trimmedLine}</h2>;
      }
      
      if (trimmedLine.startsWith('Last updated:')) {
        return <p key={index} className="text-xs text-muted-foreground mb-4">{trimmedLine}</p>;
      }
      
      if (/^\d+\.\s/.test(trimmedLine)) {
        return <h3 key={index} className="text-sm font-semibold mt-4 mb-1">{trimmedLine}</h3>;
      }
      
      if (trimmedLine.startsWith('- ')) {
        return <li key={index} className="text-xs text-muted-foreground ml-4">{trimmedLine.substring(2)}</li>;
      }
      
      return <p key={index} className="text-xs text-muted-foreground">{trimmedLine}</p>;
    });
  };

  const getTitle = () => {
    if (needsPrivacy && needsTerms) return "Legal Documents Update";
    if (needsPrivacy) return "Privacy Policy Update";
    return "Terms & Conditions Update";
  };

  const getDescription = () => {
    const docs = [];
    if (needsPrivacy) docs.push("Privacy Policy");
    if (needsTerms) docs.push("Terms & Conditions");
    return `Please review and accept our ${docs.join(" and ")} to continue using Bortal.`;
  };

  return (
    <Dialog open={open} onOpenChange={() => {}}>
      <DialogContent 
        className="sm:max-w-lg max-h-[90vh] flex flex-col" 
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>{getTitle()}</DialogTitle>
          <DialogDescription>{getDescription()}</DialogDescription>
        </DialogHeader>
        
        <ScrollArea className="flex-1 max-h-[35vh] border rounded-md p-4 bg-muted/20">
          <div className="space-y-1">
            {needsPrivacy && pendingDocuments.privacyPolicy && (
              <>
                {renderContentPreview(pendingDocuments.privacyPolicy.content)}
                {needsTerms && <div className="border-t border-border my-4" />}
              </>
            )}
            {needsTerms && pendingDocuments.terms && (
              renderContentPreview(pendingDocuments.terms.content)
            )}
          </div>
        </ScrollArea>

        <div className="space-y-3 py-4">
          {needsPrivacy && (
            <div className="flex items-start space-x-3">
              <Checkbox
                id="privacy-accept"
                checked={hasAgreedPrivacy}
                onCheckedChange={(checked) => setHasAgreedPrivacy(checked === true)}
              />
              <label
                htmlFor="privacy-accept"
                className="text-sm text-muted-foreground leading-tight cursor-pointer"
              >
                I have read and agree to the{" "}
                <a
                  href="/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:underline"
                >
                  Privacy Policy
                </a>
                {pendingDocuments.privacyPolicy && (
                  <span className="text-xs"> (version {pendingDocuments.privacyPolicy.version})</span>
                )}
              </label>
            </div>
          )}
          
          {needsTerms && (
            <div className="flex items-start space-x-3">
              <Checkbox
                id="terms-accept"
                checked={hasAgreedTerms}
                onCheckedChange={(checked) => setHasAgreedTerms(checked === true)}
              />
              <label
                htmlFor="terms-accept"
                className="text-sm text-muted-foreground leading-tight cursor-pointer"
              >
                I have read and agree to the{" "}
                <a
                  href="/terms"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:underline"
                >
                  Terms & Conditions
                </a>
                {pendingDocuments.terms && (
                  <span className="text-xs"> (version {pendingDocuments.terms.version})</span>
                )}
              </label>
            </div>
          )}
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button
            variant="outline"
            onClick={handleDecline}
            disabled={isAccepting}
          >
            Decline & Sign Out
          </Button>
          <Button
            onClick={handleAccept}
            disabled={!canAccept || isAccepting}
          >
            {isAccepting ? "Accepting..." : "Accept & Continue"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default LegalAcceptanceModal;
